import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  ChevronRight,
  ChevronUp,
  Clock,
  Shield,
  Printer,
  Search,
  X,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
} from "lucide-react";
import {
  termsSections,
  termsMetadata,
  termsTableOfContents,
  acknowledgmentText,
} from "../data/termsOfServiceData";

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(termsSections.map((s) => s.id))
  );
  const location = useLocation();

  // Handle scroll to section from URL hash
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  // Track active section and show/hide back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);

      // Find active section
      const sections = termsSections.map((s) => document.getElementById(s.id));
      const scrollPos = window.scrollY + 150;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPos) {
          setActiveSection(termsSections[i].id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handlePrint = () => {
    window.print();
  };

  // Filter sections based on search
  const filteredSections = searchQuery
    ? termsSections.filter(
        (section) =>
          section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          section.content.some((c) =>
            c.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          section.subsections?.some(
            (sub) =>
              sub.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
              sub.content.some((c) =>
                c.toLowerCase().includes(searchQuery.toLowerCase())
              )
          )
      )
    : termsSections;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-[#FAFAFA]" />

        {/* Subtle Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%238B52FF' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Animated Gradient Orbs */}
        <motion.div
          className="absolute top-20 left-[10%] w-[500px] h-[500px] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(139, 82, 255, 0.15) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-20 right-[10%] w-[600px] h-[600px] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(224, 90, 106, 0.12) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.3, 0.4, 0.3],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />

        {/* Decorative Elements */}
        <div className="absolute top-32 right-[15%] w-3 h-3 rounded-full bg-[#8B52FF]/30" />
        <div className="absolute top-48 right-[25%] w-2 h-2 rounded-full bg-[#E05A6A]/30" />
        <div className="absolute bottom-32 left-[20%] w-4 h-4 rounded-full bg-[#8B52FF]/20" />
        <div className="absolute bottom-48 left-[10%] w-2 h-2 rounded-full bg-[#E05A6A]/20" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-lg border border-gray-100 mb-8"
          >
            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] animate-pulse" />
            <span className="text-sm font-medium text-gray-700">
              Legal Document
            </span>
            <FileText className="w-4 h-4 text-[#8B52FF]" />
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6"
          >
            Terms of{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] bg-clip-text text-transparent">
                Service
              </span>
              {/* Decorative underline */}
              <motion.svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 200 12"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
              >
                <motion.path
                  d="M2 8 Q50 2, 100 8 T198 8"
                  fill="none"
                  stroke="url(#gradient)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient
                    id="gradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#8B52FF" />
                    <stop offset="100%" stopColor="#E05A6A" />
                  </linearGradient>
                </defs>
              </motion.svg>
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed"
          >
            Please read these terms carefully before using our services. By
            accessing or using Unimerg, you agree to be bound by these terms.
          </motion.p>

          {/* Meta Info Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap items-center justify-center gap-4 mb-10"
          >
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-md border border-gray-100">
              <Clock className="w-4 h-4 text-[#8B52FF]" />
              <span className="text-sm text-gray-600">
                Effective:{" "}
                <span className="font-medium text-gray-900">
                  {termsMetadata.effectiveDate}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-md border border-gray-100">
              <FileText className="w-4 h-4 text-[#8B52FF]" />
              <span className="text-sm text-gray-600">
                Version{" "}
                <span className="font-medium text-gray-900">
                  {termsMetadata.version}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-md border border-gray-100">
              <Shield className="w-4 h-4 text-[#E05A6A]" />
              <span className="text-sm text-gray-600">
                Updated:{" "}
                <span className="font-medium text-gray-900">
                  {termsMetadata.lastUpdated}
                </span>
              </span>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              onClick={handlePrint}
              className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-medium shadow-lg hover:shadow-xl hover:shadow-[#8B52FF]/25 transition-all duration-300 overflow-hidden"
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <Printer className="w-5 h-5 relative z-10" />
              <span className="relative z-10">Print Document</span>
            </button>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-16"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="flex flex-col items-center gap-2 text-gray-400"
            >
              <span className="text-xs font-medium uppercase tracking-wider">
                Scroll to read
              </span>
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="bg-amber-50 border-b border-amber-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> These Terms include an arbitration
                agreement and class action waiver that affect your legal rights.
                Please read{" "}
                <button
                  onClick={() => scrollToSection("dispute-resolution")}
                  className="text-[#8B52FF] hover:underline font-medium"
                >
                  Section 14 (Dispute Resolution)
                </button>{" "}
                carefully.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24 space-y-6">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search terms..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all text-sm"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-2xl border border-gray-100 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 px-2">
                    Table of Contents
                  </h3>
                  <nav className="space-y-1 max-h-[60vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    {termsTableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-all ${
                          activeSection === item.id
                            ? "bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 text-[#8B52FF] font-medium"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <ChevronRight
                          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${
                            activeSection === item.id ? "text-[#8B52FF]" : ""
                          }`}
                        />
                        <span className="line-clamp-1">{item.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Quick Links */}
                <div className="bg-gradient-to-br from-[#8B52FF]/5 to-[#E05A6A]/5 rounded-2xl border border-gray-100 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Related Documents
                  </h3>
                  <div className="space-y-2">
                    <Link
                      to="/privacy"
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#8B52FF] transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      Privacy Policy
                    </Link>
                    {/* <Link
                      to="/cookies"
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#8B52FF] transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      Cookie Policy
                    </Link>
                    <Link
                      to="/acceptable-use"
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#8B52FF] transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      Acceptable Use Policy
                    </Link> */}
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
                {/* Content */}
                <div className="p-6 md:p-10 prose prose-gray max-w-none">
                  {/* Search Results Info */}
                  {searchQuery && (
                    <div className="mb-8 p-4 rounded-xl bg-[#8B52FF]/5 border border-[#8B52FF]/20">
                      <p className="text-sm text-gray-700">
                        Found{" "}
                        <strong className="text-[#8B52FF]">
                          {filteredSections.length}
                        </strong>{" "}
                        section{filteredSections.length !== 1 ? "s" : ""}{" "}
                        matching "{searchQuery}"
                      </p>
                    </div>
                  )}

                  {/* Sections */}
                  {filteredSections.map((section) => (
                    <section
                      key={section.id}
                      id={section.id}
                      className="scroll-mt-24 mb-12 last:mb-0"
                    >
                      {/* Section Header */}
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="w-full flex items-center justify-between group mb-4"
                      >
                        <h2 className="text-md md:text-2xl font-bold text-gray-900 group-hover:text-[#8B52FF] transition-colors">
                          {section.title}
                        </h2>
                        <ChevronRight
                          className={`w-5 h-5 text-gray-400 transition-transform ${
                            expandedSections.has(section.id) ? "rotate-90" : ""
                          }`}
                        />
                      </button>

                      {expandedSections.has(section.id) && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          transition={{ duration: 0.2 }}
                        >
                          {/* Section Content */}
                          {section.content.map((paragraph, idx) => (
                            <p
                              key={idx}
                              className="text-gray-600 leading-relaxed mb-4"
                            >
                              {paragraph}
                            </p>
                          ))}

                          {/* Subsections */}
                          {section.subsections?.map((subsection, subIdx) => (
                            <div key={subIdx} className="mt-6 ml-4">
                              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                {subsection.title}
                              </h3>
                              {subsection.content.map((paragraph, pIdx) => (
                                <p
                                  key={pIdx}
                                  className="text-gray-600 leading-relaxed mb-3"
                                >
                                  {paragraph}
                                </p>
                              ))}
                            </div>
                          ))}

                          {/* Section Divider */}
                          <div className="mt-8 pt-8 border-t border-gray-100" />
                        </motion.div>
                      )}
                    </section>
                  ))}

                  {/* Acknowledgment Section */}
                  <section className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-[#8B52FF]/5 to-[#E05A6A]/5 border border-[#8B52FF]/20">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Acknowledgment
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {acknowledgmentText}
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Last Updated */}
                  <div className="mt-8 pt-8 border-t border-gray-100 text-center">
                    <p className="text-sm text-gray-500">
                      These Terms of Service were last updated on{" "}
                      <strong>{termsMetadata.lastUpdated}</strong>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Version {termsMetadata.version} • Governed by the laws of{" "}
                      {termsMetadata.jurisdiction}
                    </p>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Have questions about our Terms?
          </h2>
          <p className="text-gray-600 mb-8">
            Our legal team is here to help clarify any questions you may have
            about these Terms of Service.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/faqs"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:border-gray-400 transition-all"
            >
              View FAQs
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 p-3 rounded-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white shadow-lg hover:shadow-xl transition-all"
        >
          <ChevronUp className="w-5 h-5" />
        </motion.button>
      )}
    </div>
  );
};

export default TermsOfService;
